(function () {
    const BASE_ZOOM_SCALE = 2.5;
    const EXTRA_ZOOM_SCALE = 4.5;
    const MAX_ZOOM_RESOURCE_WIDTH = 3000;
    const MAX_EFFECTIVE_DPR = 2;
    const mediaAnalysisPromises = new Map();

    function getLargestSrcsetWidth(img) {
        const srcset = img.getAttribute('srcset') || '';

        return srcset.split(',').reduce((largestWidth, candidate) => {
            const widthMatch = candidate.trim().match(/\s(\d+)w$/);
            const candidateWidth = widthMatch ? parseInt(widthMatch[1], 10) : 0;
            return Math.max(largestWidth, candidateWidth);
        }, 0);
    }

    function createZoomPlan(media, img, desiredScale, preserveDesiredScale) {
        const renderedWidth = Math.max(media.getBoundingClientRect().width, 1);
        const browserDpr = Math.max(window.devicePixelRatio || 1, 1);
        const effectiveDpr = Math.min(browserDpr, MAX_EFFECTIVE_DPR);
        const originalWidth = parseInt(img.getAttribute('width'), 10) || img.naturalWidth || 1;
        const largestSrcsetWidth = getLargestSrcsetWidth(img) || originalWidth;
        const availableWidth = Math.min(originalWidth, largestSrcsetWidth, MAX_ZOOM_RESOURCE_WIDTH);
        const desiredResourceWidth = Math.ceil(renderedWidth * desiredScale * effectiveDpr);
        const resourceWidth = Math.min(desiredResourceWidth, availableWidth);
        const availableScale = resourceWidth / (renderedWidth * effectiveDpr);

        return {
            resourceWidth: resourceWidth,
            scale: preserveDesiredScale
                ? desiredScale
                : Math.max(1, Math.min(desiredScale, availableScale)),
            sizes: Math.max(1, Math.ceil(resourceWidth / browserDpr)) + 'px'
        };
    }

    function preloadZoomCandidate(img, plan) {
        return new Promise((resolve, reject) => {
            const srcset = img.getAttribute('srcset');

            if (!srcset) {
                reject(new Error('Responsive image srcset is unavailable'));
                return;
            }

            const preloadImage = new Image();
            preloadImage.decoding = 'async';
            preloadImage.fetchPriority = 'high';
            preloadImage.sizes = plan.sizes;
            preloadImage.srcset = srcset;
            preloadImage.onload = function () {
                if (typeof preloadImage.decode !== 'function') {
                    resolve();
                    return;
                }

                preloadImage.decode().then(resolve).catch(resolve);
            };
            preloadImage.onerror = reject;
            preloadImage.src = img.src;
        });
    }

    function applyZoomCandidate(img, plan) {
        img.sizes = plan.sizes;

        return new Promise((resolve) => {
            requestAnimationFrame(() => {
                if (typeof img.decode !== 'function') {
                    resolve();
                    return;
                }

                img.decode().then(resolve).catch(resolve);
            });
        });
    }

    function ensureZoomResolution(media, img, desiredScale, preserveDesiredScale) {
        const plan = createZoomPlan(media, img, desiredScale, preserveDesiredScale);
        const loadedResourceWidth = parseInt(media.dataset.zoomResourceWidth || '0', 10);

        if (loadedResourceWidth >= plan.resourceWidth) {
            return Promise.resolve(plan);
        }

        const previousRequest = media.zoomLoadPromise || Promise.resolve();
        const currentRequest = previousRequest.catch(() => undefined).then(() => {
            const currentLoadedWidth = parseInt(media.dataset.zoomResourceWidth || '0', 10);

            if (currentLoadedWidth >= plan.resourceWidth) {
                return plan;
            }

            return preloadZoomCandidate(img, plan)
                .then(() => applyZoomCandidate(img, plan))
                .then(() => {
                    media.dataset.zoomResourceWidth = String(plan.resourceWidth);
                    return plan;
                });
        });

        media.zoomLoadPromise = currentRequest;
        return currentRequest;
    }

    function initZoom() {
        const mediaItems = document.querySelectorAll('.product__media-item:not([data-infinite-loop-clone]) .media[data-media-type="image"]');
        if (!mediaItems.length) return;

        const getMediaId = (mediaContainer) => {
            const mediaItem = mediaContainer.closest('.product__media-item');

            return mediaContainer.dataset.mediaId
                || (mediaItem ? mediaItem.dataset.mediaItemId : '')
                || '';
        };

        const getAnalysisSource = (mediaContainer) => {
            const mediaId = getMediaId(mediaContainer);
            const thumbnailButtons = document.querySelectorAll('[data-thumbnail-id]');
            let thumbnailFallback = '';

            for (let index = 0; index < thumbnailButtons.length; index += 1) {
                if (thumbnailButtons[index].dataset.thumbnailId !== mediaId) continue;

                const thumbnailImage = thumbnailButtons[index].querySelector('img');
                if (thumbnailImage) {
                    if (thumbnailImage.currentSrc) return thumbnailImage.currentSrc;
                    thumbnailFallback = thumbnailFallback || thumbnailImage.src;
                }
            }

            if (thumbnailFallback) return thumbnailFallback;

            const image = mediaContainer.querySelector('img');
            return image ? image.currentSrc || image.src : '';
        };

        const applyAnalysisResult = (mediaContainer, result) => {
            const mediaId = getMediaId(mediaContainer);
            const matchingMedia = mediaId
                ? Array.from(mediaItems).filter((candidate) => getMediaId(candidate) === mediaId)
                : [mediaContainer];

            matchingMedia.forEach((candidate) => {
                candidate.dataset.zoomMode = result.zoomMode;
                candidate.dataset.mediaPresentation = result.presentation;

                const mediaItem = candidate.closest('.product__media-item');
                if (mediaItem) {
                    mediaItem.dataset.mediaPresentation = result.presentation;
                }
            });

            if (mediaId) {
                document.querySelectorAll('[data-infinite-loop-clone]').forEach((clone) => {
                    if (clone.dataset.loopSourceMediaId !== mediaId) return;

                    clone.dataset.mediaPresentation = result.presentation;
                    const cloneMedia = clone.querySelector('.media');
                    if (cloneMedia) {
                        cloneMedia.dataset.zoomMode = result.zoomMode;
                        cloneMedia.dataset.mediaPresentation = result.presentation;
                    }
                });
            }
        };

        const analyzeMedia = (mediaContainer) => {
            return new Promise((resolve) => {
                const finish = (presentation) => {
                    resolve({
                        presentation: presentation,
                        zoomMode: presentation === 'packshot' ? 'centered' : 'normal'
                    });
                };
                const source = getAnalysisSource(mediaContainer);

                if (!source) {
                    finish('natural');
                    return;
                }

                const proxyImage = new Image();
                proxyImage.crossOrigin = 'Anonymous';
                proxyImage.onload = function () {
                    try {
                        const canvas = document.createElement('canvas');
                        const context = canvas.getContext('2d');
                        const sampleSize = 50;
                        canvas.width = sampleSize;
                        canvas.height = sampleSize;
                        context.drawImage(proxyImage, 0, 0, sampleSize, sampleSize);

                        const pixels = context.getImageData(0, 0, sampleSize, sampleSize).data;
                        const getPixel = (x, y) => {
                            const pixelIndex = (y * sampleSize + x) * 4;
                            return [
                                pixels[pixelIndex],
                                pixels[pixelIndex + 1],
                                pixels[pixelIndex + 2],
                                pixels[pixelIndex + 3]
                            ];
                        };
                        const isBackground = (rgba) => {
                            return rgba[3] < 24 || (rgba[0] > 238 && rgba[1] > 238 && rgba[2] > 238);
                        };
                        const cornerPixels = [
                            getPixel(0, 0),
                            getPixel(sampleSize - 1, 0),
                            getPixel(0, sampleSize - 1),
                            getPixel(sampleSize - 1, sampleSize - 1)
                        ];
                        const lightCornerCount = cornerPixels.filter(isBackground).length;

                        if (lightCornerCount < 3) {
                            finish('natural');
                            return;
                        }

                        const scanLines = [
                            Math.floor(sampleSize * 0.35),
                            Math.floor(sampleSize * 0.5),
                            Math.floor(sampleSize * 0.65)
                        ];
                        let minX = sampleSize;
                        let maxX = -1;

                        for (let x = 0; x < sampleSize; x += 1) {
                            const hasContent = scanLines.some((y) => !isBackground(getPixel(x, y)));
                            if (!hasContent) continue;

                            minX = Math.min(minX, x);
                            maxX = Math.max(maxX, x);
                        }

                        if (maxX < minX) {
                            finish('natural');
                            return;
                        }

                        const contentWidthRatio = (maxX - minX + 1) / sampleSize;
                        finish(contentWidthRatio <= 0.55 ? 'packshot' : 'natural');
                    } catch (error) {
                        console.warn('Smart Zoom: Unable to classify product image', error);
                        finish('natural');
                    }
                };
                proxyImage.onerror = function () {
                    console.warn('Smart Zoom: Failed to load product image for classification');
                    finish('natural');
                };
                proxyImage.src = source;
            });
        };

        const checkImageBackground = (mediaContainer) => {
            const mediaId = getMediaId(mediaContainer);
            const cacheKey = mediaId || getAnalysisSource(mediaContainer);

            if (!mediaAnalysisPromises.has(cacheKey)) {
                mediaAnalysisPromises.set(cacheKey, analyzeMedia(mediaContainer));
            }

            return mediaAnalysisPromises.get(cacheKey).then((result) => {
                applyAnalysisResult(mediaContainer, result);
                return result.zoomMode;
            });
        };

        const isNearViewport = (mediaContainer) => {
            const bounds = mediaContainer.getBoundingClientRect();
            return bounds.width > 0
                && bounds.height > 0
                && bounds.bottom >= 0
                && bounds.top <= window.innerHeight * 1.25;
        };
        const uniqueMediaById = new Map();

        Array.from(mediaItems).forEach((mediaContainer) => {
            const mediaId = getMediaId(mediaContainer) || getAnalysisSource(mediaContainer);
            const existingMedia = uniqueMediaById.get(mediaId);

            if (!existingMedia || (!isNearViewport(existingMedia) && isNearViewport(mediaContainer))) {
                uniqueMediaById.set(mediaId, mediaContainer);
            }
        });

        const uniqueMediaItems = Array.from(uniqueMediaById.values());
        const immediateMediaItems = [];
        const deferredMediaItems = [];

        uniqueMediaItems.forEach((mediaContainer) => {
            if (isNearViewport(mediaContainer)) {
                immediateMediaItems.push(mediaContainer);
            } else {
                deferredMediaItems.push(mediaContainer);
            }
        });

        immediateMediaItems.forEach(checkImageBackground);

        const classifyDeferredMedia = () => {
            deferredMediaItems.forEach(checkImageBackground);
        };

        if (typeof window.requestIdleCallback === 'function') {
            window.requestIdleCallback(classifyDeferredMedia, { timeout: 1500 });
        } else {
            setTimeout(classifyDeferredMedia, 500);
        }

        if (typeof window.matchMedia === 'function' && window.matchMedia('(hover: none)').matches) return;
        if (window.innerWidth < 960) return;

        mediaItems.forEach(media => {
            if (media.dataset.zoomInitialized) return;
            media.dataset.zoomInitialized = "true";
            const img = media.querySelector('img');
            if (!img) return;

            let isHovered = false;
            let extraZoomPending = false;
            let zoomRequestId = 0;
            let pointerFrame = null;
            let latestPointerEvent = null;

            media.addEventListener('mouseenter', function () {
                const requestId = ++zoomRequestId;
                const initialPlan = createZoomPlan(media, img, BASE_ZOOM_SCALE);
                isHovered = true;
                media.classList.add('is-zoom-loading');
                const zoomModePromise = checkImageBackground(media);

                zoomModePromise.then((zoomMode) => {
                    if (!isHovered || requestId !== zoomRequestId) return;

                    media.baseZoomScale = initialPlan.scale;
                    media.style.setProperty('--zoom-scale', initialPlan.scale);
                    if (zoomMode === 'centered') {
                        media.style.setProperty('--zoom-x', '50%');
                    }
                    media.classList.remove('is-zoom-loading');

                    if (initialPlan.scale > 1) {
                        media.classList.add('is-zooming');
                    }
                });

                ensureZoomResolution(media, img, BASE_ZOOM_SCALE).catch((error) => {
                    console.warn('Smart Zoom: Unable to load adaptive image', error);
                });
            });

            media.addEventListener('mousemove', function (e) {
                latestPointerEvent = e;
                if (pointerFrame) return;

                pointerFrame = requestAnimationFrame(() => {
                    const rect = media.getBoundingClientRect();
                    let x = ((latestPointerEvent.clientX - rect.left) / rect.width) * 100;
                    const y = ((latestPointerEvent.clientY - rect.top) / rect.height) * 100;

                    if (media.dataset.zoomMode === 'centered' && !media.classList.contains('is-extra-zoomed')) {
                        x = 50;
                    }

                    media.style.setProperty('--zoom-x', x + '%');
                    media.style.setProperty('--zoom-y', y + '%');
                    pointerFrame = null;
                });
            });

            media.addEventListener('click', function (e) {
                if (!media.classList.contains('is-zooming')) return;

                if (media.classList.contains('is-extra-zoomed')) {
                    e.preventDefault();
                    e.stopPropagation();
                    media.classList.remove('is-extra-zoomed');
                    media.style.setProperty('--zoom-scale', media.baseZoomScale || BASE_ZOOM_SCALE);
                    return;
                }

                if (extraZoomPending) {
                    e.preventDefault();
                    e.stopPropagation();
                    return;
                }

                const extraZoomPlan = createZoomPlan(media, img, EXTRA_ZOOM_SCALE, true);
                const baseZoomScale = media.baseZoomScale || 1;

                if (extraZoomPlan.scale <= baseZoomScale + 0.05) return;

                e.preventDefault();
                e.stopPropagation();

                const requestId = ++zoomRequestId;
                extraZoomPending = true;
                media.classList.add('is-zoom-loading');

                ensureZoomResolution(media, img, EXTRA_ZOOM_SCALE, true).then((plan) => {
                    if (!isHovered || requestId !== zoomRequestId) return;

                    extraZoomPending = false;
                    media.classList.remove('is-zoom-loading');
                    media.style.setProperty('--zoom-scale', plan.scale);
                    media.classList.add('is-extra-zoomed');
                }).catch((error) => {
                    if (requestId === zoomRequestId) {
                        extraZoomPending = false;
                        media.classList.remove('is-zoom-loading');
                    }
                    console.warn('Smart Zoom: Unable to load extra adaptive image', error);
                });
            });

            media.addEventListener('mouseleave', function () {
                isHovered = false;
                extraZoomPending = false;
                zoomRequestId += 1;
                media.classList.remove('is-zooming');
                media.classList.remove('is-extra-zoomed');
                media.classList.remove('is-zoom-loading');
                media.style.setProperty('--zoom-x', '50%');
                media.style.setProperty('--zoom-y', '50%');

                if (pointerFrame) {
                    cancelAnimationFrame(pointerFrame);
                    pointerFrame = null;
                }
            });
        });
    }
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initZoom);
    } else {
        initZoom();
    }
    document.addEventListener('shopify:section:load', initZoom);
    setTimeout(initZoom, 1000);
})();
